import { useEffect, useRef, useState } from 'react';
import Reports from '../../services/reports';
import toaster from '../../utils/toaster';
import { downloadCSV } from '../../utils/csvExport';
import { parseCSVLine } from '../../utils/csvUtils';
import { CHAT_EVENTS, trackChatEvent } from '../constants';
import { fetchArtifactContent } from './useArtifactContent';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT.GRID';
const DEFAULT_EXPORT_FILENAME = 'report_data';
const COPY_FLASH_MS = 1500;

const reportsAPI = new Reports();

export const GSHEET_NETWORK_DATA = {
    id: 0,
    uan_id: 0,
    adn_id: 0,
    auth_type: 'google_sheets',
    display_name: '',
    name: 'gsheet',
    is_login_error: false,
    logo_path: '',
};

const buildCsvFilename = title => {
    const slug = (title || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    return `${slug || DEFAULT_EXPORT_FILENAME}.csv`;
};

// Copy / CSV-download / Google-Sheets export for a single grid artifact.
const useGridExport = ({ data, metadata, contentUrl, title, translate }) => {
    const pendingSheetsCsvRef = useRef(null);
    const oauthRef = useRef(null);
    const authTriggeredRef = useRef(false);
    const [copied, setCopied] = useState(false);
    const [copyLoading, setCopyLoading] = useState(false);
    const [exportType, setExportType] = useState(null); // 'csv' | 'sheets' while an export is running
    const [sheetsAuthActive, setSheetsAuthActive] = useState(false);

    useEffect(() => {
        if (!copied) return undefined;
        const timeoutId = setTimeout(() => setCopied(false), COPY_FLASH_MS);
        return () => clearTimeout(timeoutId);
    }, [copied]);

    const needsFetch = Boolean(metadata?.is_truncated && contentUrl);
    const resolveExportCsv = () => (needsFetch ? fetchArtifactContent(contentUrl) : Promise.resolve(data));

    // Prefer the backend's row_count/columns over re-parsing the whole CSV on every export.
    const getCsvShape = csv => {
        const rows = metadata?.row_count;
        const columns = metadata?.columns?.length;
        if (Number.isFinite(rows) && columns > 0) {
            return { rows, columns };
        }
        const lines = csv.split('\n').filter(line => line.trim());
        return { rows: Math.max(0, lines.length - 1), columns: parseCSVLine(lines[0] || '').length };
    };

    const handleCopy = async () => {
        if (copyLoading) return;
        if (needsFetch) setCopyLoading(true);
        try {
            const csv = await resolveExportCsv();
            await navigator.clipboard.writeText(csv);
            setCopied(true);
            trackChatEvent(CHAT_EVENTS.COPY_CSV, getCsvShape(csv));
        } catch (err) {
            console.error('Failed to copy CSV content:', err);
        } finally {
            if (needsFetch) setCopyLoading(false);
        }
    };

    const handleDownloadCsv = async () => {
        if (exportType) return;
        setExportType('csv');
        try {
            const csv = await resolveExportCsv();
            downloadCSV(csv, buildCsvFilename(title));
            trackChatEvent(CHAT_EVENTS.DOWNLOAD_CSV, getCsvShape(csv));
        } catch (err) {
            console.error('Failed to download CSV content:', err);
        } finally {
            setExportType(null);
        }
    };

    const runSheetsExport = async token => {
        const csv = pendingSheetsCsvRef.current;
        if (csv == null) return;
        setExportType('sheets');
        try {
            const result = await reportsAPI.exportDataToGsheet(title || DEFAULT_EXPORT_FILENAME, token, csv);
            if (result?.error_message) {
                toaster.error(translate(`${TRANSLATION_PREFIX}.EXPORT_GSHEET_FAILED`));
                return;
            }
            if (result?.gsheet_url) {
                const sheetWindow = window.open('about:blank', '_blank');
                if (sheetWindow) {
                    sheetWindow.location.href = result.gsheet_url;
                    sheetWindow.focus();
                }
            }
            trackChatEvent(CHAT_EVENTS.EXPORT_GOOGLE_SHEETS, getCsvShape(csv));
        } catch (err) {
            console.error('Failed to export to Google Sheets:', err);
            toaster.error(translate(`${TRANSLATION_PREFIX}.EXPORT_GSHEET_FAILED`));
        } finally {
            pendingSheetsCsvRef.current = null;
            setExportType(null);
            setSheetsAuthActive(false);
        }
    };

    const handleSheetsAuth = authData => {
        if (!authData?.refresh_token) {
            pendingSheetsCsvRef.current = null;
            setSheetsAuthActive(false);
            return false;
        }
        runSheetsExport(authData.refresh_token);
        return true;
    };

    // The OAuth popup can fail to load its auth URL without ever calling back.
    // Clear the auth latch so Sheets export isn't permanently stuck for this grid.
    const handleSheetsAuthError = err => {
        console.error('Failed to start Google Sheets authorization:', err);
        toaster.error(translate(`${TRANSLATION_PREFIX}.EXPORT_GSHEET_FAILED`));
        pendingSheetsCsvRef.current = null;
        setSheetsAuthActive(false);
    };

    const handleExportSheets = async () => {
        if (exportType) return;
        // Auth is already pending (e.g. the user closed the popup without
        // completing it); re-open the OAuth popup instead of silently no-op'ing.
        if (sheetsAuthActive) {
            oauthRef.current?.oauthClick();
            return;
        }
        if (needsFetch) setExportType('sheets');
        let csv;
        try {
            csv = await resolveExportCsv();
        } catch (err) {
            console.error('Failed to load CSV content for Google Sheets export:', err);
            toaster.error(translate(`${TRANSLATION_PREFIX}.EXPORT_GSHEET_FAILED`));
            setExportType(null);
            return;
        }
        setExportType(null);
        pendingSheetsCsvRef.current = csv;
        setSheetsAuthActive(true);
    };

    useEffect(() => {
        if (!sheetsAuthActive) {
            authTriggeredRef.current = false;
            return;
        }
        if (oauthRef.current && !authTriggeredRef.current) {
            authTriggeredRef.current = true;
            oauthRef.current.oauthClick();
        }
    }, [sheetsAuthActive]);

    return {
        copied,
        copyLoading,
        exportType,
        sheetsAuthActive,
        oauthRef,
        handleCopy,
        handleDownloadCsv,
        handleExportSheets,
        handleSheetsAuth,
        handleSheetsAuthError,
    };
};

export default useGridExport;
